import React, { useContext, useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { ThemeContext } from '../contexts/ThemeContext';
import { LayoutDashboard, Wallet, Clock, Users, LogOut, Package, Moon, Sun, FileVideo, BarChart3, ClipboardList, ClipboardCheck, BellRing, Truck, IdCard, Handshake, MessageSquareText, Menu, X } from 'lucide-react';
import { canAccessBossModules, getRoleLabel, normalizeRole } from '../utils/roleUtils';

const Layout = ({ children }) => {
    const { logout, user } = useContext(AuthContext);
    const { theme, toggleTheme } = useContext(ThemeContext);
    const navigate = useNavigate();
    const location = useLocation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const normalizedRole = normalizeRole(user?.role);

    const handleLogout = () => {
        setIsMobileMenuOpen(false);
        logout();
        navigate('/login');
    };

    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (!isMobileMenuOpen) return undefined;

        const handleEscape = (event) => {
            if (event.key === 'Escape') setIsMobileMenuOpen(false);
        };

        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [isMobileMenuOpen]);

    let navItems = [];

    if (normalizedRole === 'shipment_staff') {
        navItems.push({ name: 'Sevkiyat', path: '/shipments', icon: <Package size={20} /> });
        navItems.push({ name: 'Sevke Hazır', path: '/ready-to-ship', icon: <Truck size={20} /> });
    } else {
        const canSeeBossModules = canAccessBossModules(normalizedRole);

        navItems = [
            { name: 'Özet', path: '/dashboard', icon: <LayoutDashboard size={20} /> },
            { name: 'Tahsilatlar', path: '/collections', icon: <Wallet size={20} /> },
            { name: 'Bekleyen', path: '/aging', icon: <Clock size={20} /> },
            { name: 'Dosyalar', path: '/files', icon: <FileVideo size={20} /> },
        ];
        navItems.push({ name: 'Sevkiyat', path: '/shipments', icon: <Package size={20} /> });
        navItems.push({ name: 'Sevke Hazır', path: '/ready-to-ship', icon: <Truck size={20} /> });

        if (canSeeBossModules) {
            navItems.push({ name: 'Tahsilat Raporu', path: '/collection-reports', icon: <BarChart3 size={20} /> });
            navItems.push({ name: 'Sevkiyat Raporu', path: '/shipment-reports', icon: <ClipboardList size={20} /> });
            navItems.push({ name: 'Mutabakat', path: '/reconciliations', icon: <Handshake size={20} /> });
        }

        if (canSeeBossModules || normalizedRole === 'staff') {
            navItems.push({ name: 'İdari Takip', path: '/company-tasks', icon: <ClipboardCheck size={20} /> });
        }

        if (normalizedRole === 'admin') {
            navItems.push({ name: 'Personel', path: '/personnel', icon: <IdCard size={20} /> });
            navItems.push({ name: 'Mobil Mesajlar', path: '/mobile-announcements', icon: <BellRing size={20} /> });
            navItems.push({ name: 'Bildirimler', path: '/notifications', icon: <MessageSquareText size={20} /> });
            navItems.push({ name: 'Kullanıcılar', path: '/users', icon: <Users size={20} /> });
        }
    }

    const primaryMobileItems = navItems.slice(0, 4);
    const isMoreSectionActive = !primaryMobileItems.some((item) => item.path === location.pathname);

    return (
        <div className="app-shell flex bg-dark-bg text-dark-text">

            {/* MASAÜSTÜ SIDEBAR */}
            <aside className="hidden lg:flex flex-col w-64 bg-dark-card border-r border-dark-border h-full p-4 shrink-0 transition-all z-20 overflow-y-auto overscroll-contain">
                <div className="flex flex-col gap-2 px-2 mb-8 mt-2">
                    <div className="bg-white/95 rounded-xl p-3 shadow-lg flex items-center justify-center">
                        <img src="/logo.png" alt="Öz Yürek Tekstil" className="w-full max-w-[140px] h-auto object-contain" />
                    </div>
                </div>

                <nav className="flex-1 space-y-2">
                    {navItems.map((item) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium ${isActive
                                    ? 'bg-primary-500/10 text-primary-500'
                                    : 'text-dark-text hover:text-primary-500 hover:bg-dark-hover'
                                }`
                            }
                        >
                            {item.icon}
                            <span>{item.name}</span>
                        </NavLink>
                    ))}
                </nav>

                {/* Tema Değiştirme ve Kullanıcı Bilgisi */}
                <div className="pt-4 mt-auto border-t border-dark-border space-y-3">
                    <button
                        onClick={toggleTheme}
                        className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-dark-text transition-all font-medium border border-dark-border hover:bg-dark-hover"
                    >
                        <span className="flex items-center gap-3">
                            {theme === 'ozyurek' ? <Sun size={20} className="text-yellow-500" /> : <Moon size={20} className="text-blue-400" />}
                            <span>{theme === 'ozyurek' ? 'Aydınlık Tema' : 'Karanlık Tema'}</span>
                        </span>
                    </button>

                    <div className="flex items-center gap-3 px-2">
                        <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                            {user?.username?.charAt(0).toUpperCase()}
                        </div>
                        <div className="overflow-hidden">
                            <p className="text-sm font-semibold text-dark-text truncate">{user?.username}</p>
                            <p className="text-xs text-dark-muted capitalize">
                                {getRoleLabel(normalizedRole)}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-3 text-red-500 transition-colors font-medium rounded-xl hover:bg-red-500/10"
                    >
                        <LogOut size={20} />
                        <span>Çıkış Yap</span>
                    </button>
                </div>
            </aside>

            {/* ANA İÇERİK */}
            <main className="app-main-scroll min-w-0 flex-1 p-4 sm:p-6 lg:p-8 relative isolate">
                <div className="fixed top-[-20%] left-[20%] w-[500px] h-[500px] bg-primary-500/20 rounded-full mix-blend-screen filter blur-[100px] opacity-20 pointer-events-none"></div>
                {children}
            </main>

            {/* TELEFON / TABLET ALT MENÜSÜ */}
            <nav className="mobile-bottom-nav lg:hidden fixed bottom-0 left-0 right-0 glass border-t border-dark-border z-40 pb-safe" aria-label="Ana navigasyon">
                <div className="flex items-stretch px-1 py-1.5 gap-0.5">
                    {primaryMobileItems.map((item) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            className={({ isActive }) =>
                                `flex min-h-12 flex-1 flex-col items-center justify-center gap-0.5 px-1 py-1.5 rounded-lg transition-all min-w-0 ${isActive
                                    ? 'text-primary-500 bg-primary-500/10'
                                    : 'text-dark-muted active:text-dark-text'
                                }`
                            }
                        >
                            {React.cloneElement(item.icon, { size: 18 })}
                            <span className="max-w-full truncate text-[10px] font-medium leading-tight">{item.name}</span>
                        </NavLink>
                    ))}

                    <button
                        type="button"
                        onClick={() => setIsMobileMenuOpen(true)}
                        className={`flex min-h-12 flex-1 flex-col items-center justify-center gap-0.5 px-1 py-1.5 rounded-lg transition-all min-w-0 ${isMoreSectionActive ? 'text-primary-500 bg-primary-500/10' : 'text-dark-muted active:text-dark-text'}`}
                        aria-haspopup="dialog"
                        aria-expanded={isMobileMenuOpen}
                    >
                        <Menu size={19} />
                        <span className="text-[10px] font-medium leading-tight">Menü</span>
                    </button>
                </div>
            </nav>

            {isMobileMenuOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-end bg-black/70 backdrop-blur-sm lg:hidden"
                    role="presentation"
                    onClick={() => setIsMobileMenuOpen(false)}
                >
                    <section
                        role="dialog"
                        aria-modal="true"
                        aria-label="Tüm bölümler"
                        className="flex max-h-[calc(100dvh-0.5rem)] w-full flex-col overflow-hidden rounded-t-3xl border border-dark-border bg-dark-card shadow-2xl"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="flex items-center justify-between border-b border-dark-border px-5 py-4">
                            <div className="min-w-0">
                                <p className="truncate font-bold text-dark-text">{user?.username}</p>
                                <p className="text-xs text-dark-muted">{getRoleLabel(normalizedRole)} · Tüm bölümler</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-dark-muted transition-colors hover:bg-dark-hover hover:text-dark-text"
                                aria-label="Menüyü kapat"
                            >
                                <X size={22} />
                            </button>
                        </div>

                        <div className="overflow-y-auto overscroll-contain p-4 pb-safe-offset">
                            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                                {navItems.map((item) => (
                                    <NavLink
                                        key={item.path}
                                        to={item.path}
                                        className={({ isActive }) =>
                                            `flex min-h-14 items-center gap-3 rounded-xl border px-3 py-3 text-sm font-semibold transition-colors ${isActive
                                                ? 'border-primary-500/40 bg-primary-500/10 text-primary-500'
                                                : 'border-dark-border bg-dark-bg/40 text-dark-text hover:bg-dark-hover'
                                            }`
                                        }
                                    >
                                        {React.cloneElement(item.icon, { size: 20, className: 'shrink-0' })}
                                        <span>{item.name}</span>
                                    </NavLink>
                                ))}
                            </div>

                            <div className="mt-4 grid grid-cols-2 gap-2 border-t border-dark-border pt-4">
                                <button
                                    type="button"
                                    onClick={toggleTheme}
                                    className="flex min-h-12 items-center justify-center gap-2 rounded-xl border border-dark-border bg-dark-bg/40 px-3 font-semibold text-dark-text"
                                >
                                    {theme === 'ozyurek' ? <Sun size={20} className="text-yellow-500" /> : <Moon size={20} className="text-blue-400" />}
                                    Tema
                                </button>
                                <button
                                    type="button"
                                    onClick={handleLogout}
                                    className="flex min-h-12 items-center justify-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-3 font-semibold text-red-400"
                                >
                                    <LogOut size={20} />
                                    Çıkış Yap
                                </button>
                            </div>
                        </div>
                    </section>
                </div>
            )}
        </div>
    );
};

export default Layout;
